import { useId, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, CircleAlert, Info } from "lucide-react";
import { translateShared } from "../i18n/sharedCatalog";
import { useWorkspaceCopy } from "../i18n/workspaceCatalog";
import {
  localizeFeedbackState,
  type FeedbackState,
  type FeedbackTone,
} from "./feedback";
import { usePlatformLayer } from "./useDismissibleLayer";
import { useModalBehavior } from "./useModalBehavior";

function classNames(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

type SharedLayerProps = {
  open: boolean;
  onDismiss: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  className?: string;
  role?: "dialog" | "alertdialog";
};

export function Dialog({
  open,
  onDismiss,
  title,
  description,
  children,
  footer,
  dismissible = true,
  initialFocusRef,
  className,
  role = "dialog",
}: SharedLayerProps) {
  const { locale } = useWorkspaceCopy();
  const closeLabel = translateShared(locale, "common.close");
  const titleId = useId();
  const descriptionId = useId();
  const backdropRef = usePlatformLayer<HTMLDivElement>(open, () => onDismiss(), {
    containFirstElementOnly: true,
    dismissOnEscape: dismissible,
    dismissOnOutside: dismissible,
    restoreFocus: true,
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalBehavior(open, dialogRef, initialFocusRef, backdropRef);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div ref={backdropRef} className="kavoosh-ui kavoosh-layer-backdrop kavoosh-layer-backdrop--dialog">
      <div
        ref={dialogRef}
        className={classNames("kavoosh-dialog", className)}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className="kavoosh-layer__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          {dismissible ? <button type="button" className="kavoosh-layer__close" aria-label={closeLabel} onClick={onDismiss}>×</button> : null}
        </header>
        <div className="kavoosh-layer__body">{children}</div>
        {footer ? <footer className="kavoosh-layer__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export type DrawerProps = SharedLayerProps & {
  modal?: boolean;
};

export function Drawer({
  open,
  onDismiss,
  title,
  description,
  children,
  footer,
  dismissible = true,
  initialFocusRef,
  className,
  modal = false,
}: DrawerProps) {
  const { locale } = useWorkspaceCopy();
  const closeLabel = translateShared(locale, "common.close");
  const titleId = useId();
  const descriptionId = useId();
  const backdropRef = usePlatformLayer<HTMLDivElement>(open, () => onDismiss(), {
    containFirstElementOnly: true,
    dismissOnEscape: dismissible,
    dismissOnOutside: dismissible,
    restoreFocus: true,
  });
  const drawerRef = useRef<HTMLDivElement>(null);
  useModalBehavior(open && modal, drawerRef, initialFocusRef, backdropRef);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={backdropRef}
      className={classNames(
        "kavoosh-ui kavoosh-layer-backdrop kavoosh-layer-backdrop--drawer",
        !modal && "is-nonmodal",
      )}
    >
      <aside
        ref={drawerRef}
        className={classNames("kavoosh-drawer", className)}
        role="dialog"
        aria-modal={modal ? "true" : undefined}
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className="kavoosh-layer__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          {dismissible ? <button type="button" className="kavoosh-layer__close" aria-label={closeLabel} onClick={onDismiss}>×</button> : null}
        </header>
        <div className="kavoosh-layer__body">{children}</div>
        {footer ? <footer className="kavoosh-layer__footer">{footer}</footer> : null}
      </aside>
    </div>,
    document.body,
  );
}

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  destructive?: boolean;
  busy?: boolean;
};

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  destructive = false,
  busy = false,
}: ConfirmDialogProps) {
  const { locale } = useWorkspaceCopy();
  const resolvedCancelLabel = cancelLabel ?? translateShared(locale, "common.cancel");
  const cancelRef = useRef<HTMLButtonElement>(null);
  return <Dialog
    open={open}
    onDismiss={onCancel}
    title={title}
    dismissible={!busy}
    initialFocusRef={cancelRef}
    footer={<>
      <button ref={cancelRef} type="button" className="kavoosh-button kavoosh-button--secondary" onClick={onCancel} disabled={busy}>{resolvedCancelLabel}</button>
      <button
        type="button"
        className={classNames("kavoosh-button", destructive ? "kavoosh-button--danger" : "kavoosh-button--primary")}
        onClick={onConfirm}
        disabled={busy}
      >{confirmLabel}</button>
    </>}
  >
    <p>{description}</p>
  </Dialog>;
}


function FeedbackIcon({ tone }: { tone: FeedbackTone }) {
  if (tone === "danger") return <CircleAlert size={22} aria-hidden="true" />;
  if (tone === "warning") return <AlertTriangle size={22} aria-hidden="true" />;
  if (tone === "success") return <CheckCircle2 size={22} aria-hidden="true" />;
  return <Info size={22} aria-hidden="true" />;
}

export function FeedbackDialog({
  state,
  onDismiss,
}: {
  state: FeedbackState | null;
  onDismiss: () => void;
}) {
  const { locale } = useWorkspaceCopy();
  if (!state) return null;
  const localized = localizeFeedbackState(locale, state);
  const dismissLabel = translateShared(locale, "feedback.dismiss");
  const role = localized.tone === "danger" || localized.tone === "warning"
    ? "alertdialog"
    : "dialog";
  return <Dialog
    open
    onDismiss={onDismiss}
    title={localized.title}
    role={role}
    className={classNames("kavoosh-feedback-dialog", `is-${localized.tone}`)}
    footer={<>
      {localized.action ? <button
        type="button"
        className="kavoosh-button kavoosh-button--primary"
        onClick={localized.action.onAction}
      >{localized.action.label}</button> : null}
      <button
        type="button"
        className="kavoosh-button kavoosh-button--secondary"
        onClick={onDismiss}
      >{dismissLabel}</button>
    </>}
  >
    <div className="kavoosh-feedback-dialog__message">
      <span className="kavoosh-feedback-dialog__icon">
        <FeedbackIcon tone={localized.tone} />
      </span>
      <p>{localized.message}</p>
    </div>
  </Dialog>;
}
